/* eslint-disable @typescript-eslint/consistent-type-assertions */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Box, Breadcrumbs, Button, Grid, Link, Stack, Typography } from '@mui/material'
import { useLocation, useNavigate, useParams } from 'react-router'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { IncomeVerification } from 'Components/Program/IncomeVerification'
import { GeneralDocumentAccordion } from 'Components/DocumentList/GeneralDocumentAccordion'
import { ProgramVerification } from 'Components/Program/ProgramVerification'
import { useEffect, useState } from 'react'
import { TextAreaWithLabel } from 'Components/TextArea'
import { LargeCard } from 'Components/Cards/Large'
import { sendGet, sendPostFormData } from 'hooks/use-fetch'
import { toast } from 'react-toastify'
import { type ProgramVerificationDetails as pvd, type postError, DocumentTarget } from 'core/types'
import { type DocumentCaregiver, type DocumentData } from 'Components/DocumentList/DocumentInfo'
import { useRefresh, useRefreshDispatch } from 'ContextProviders/RefreshProvider'
export interface DocumentFile {
  doc: DocumentData
  documentTypeId: number
  file?: File
  editMode: boolean
}

export function ProgramVerificationDetails (): JSX.Element {
  const { id } = useParams()
  const nav = useNavigate()
  const refreshTime = useRefresh()
  const location = useLocation()
  const returnText = location.state.redirectText
  const readOnly = location.state?.readOnly === true
  const refreshDispatch = useRefreshDispatch()
  const doRefresh = (): void => { refreshDispatch({ type: 'refresh' }) }
  const [files, setFiles] = useState<DocumentFile[]>([])
  const [caregivers, setCaregivers] = useState<Array<{ id: number, name: string }>>([])
  let years: number[] = []
  const [index, setIndex] = useState(-1)

  const handleFilesChanged = async (documentTypeId: number, newFileArray: DocumentFile[]): Promise<void> => {
    const otherFiles = files.filter(f => f.documentTypeId !== documentTypeId)
    const allFiles = [...otherFiles, ...newFileArray]
    setFiles(allFiles)
    const income = allFiles.reduce((accumulator, doc) => accumulator + Number(doc.doc.incomeRelated ? doc.doc.incomeAmount ?? 0 : 0), 0)
    setVerificationInfo({ ...verificationInfo, verifiedAnnualIncome: income })
  }
  const handleDiscrepencyApprovalChange = async (newVal: boolean): Promise<void> => {
    setVerificationInfo({ ...verificationInfo, acknowledgeIncomeGap: newVal })
  }
  const goBack = (): void => {
    nav(-1)
  }
  const save = async (): Promise<void> => {
    const fileData = new FormData()
    const existingDocs = files.filter(f => f.file == null).map(d => d.doc)
    const newDocs = files.filter(f => f.file != null)
    const errors: string[] = []
    const justDocInfo: DocumentData[] = newDocs.map(d => {
      return { ...d.doc, DocumentTypeID: d.documentTypeId }
    })

    if (verificationInfo.requiredDocType == null ||
      files.filter(f => f.documentTypeId === verificationInfo.requiredDocType).length === 0) {
      errors.push('Verification requires at least one income related document.')
    }
    verificationInfo?.optionalDocTypes.forEach(docType => {
      if (files.filter(f => f.documentTypeId === docType.documentTypeID).length === 0) {
        errors.push(`Document type of ${docType.name} requires at least one document`)
      }
    })
    if (errors.length > 0) {
      const str = errors.reduce(
        (current, next) => current + ' ' + next,
        ''
      )
      toast.error('Unable to submit verification: ' + str)
      return
    }
    const justFiles = newDocs.map(d => d.file)
    fileData.append('id', id ?? '')
    fileData.append('attachedDocuments', JSON.stringify(existingDocs))
    fileData.append('newDocuments', JSON.stringify(justDocInfo))
    fileData.append('verificationInfo', JSON.stringify(verificationInfo))
    justFiles.forEach(f => {
      if (f != null) { fileData.append('files', f) }
    })
    const { response } = await sendPostFormData('/ProgramVerification/Save', fileData)
    const result: { message: string, errors: postError[], success: boolean } = response

    if (result.success ?? false) {
      toast.success('Verification Updated')
      doRefresh()
    } else {
      result.errors.map(e => toast.error(e.error))
    }
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const [verificationInfo, setVerificationInfo] = useState<pvd>({} as pvd)
  if ((verificationInfo?.timeSpanYears?.length ?? 0) !== 0) {
    years = verificationInfo?.timeSpanYears?.map(y => parseInt(y))?.sort((a: number, b: number) => a - b) ?? []
  } else {
    for (let i = 0; i < 4; i++) {
      years.push(new Date().getFullYear() - i)
    }
  }

  const loadData = async (): Promise<void> => {
    const { response } = await sendGet(`/ProgramVerification/Find?id=${id ?? ''}`, {})
    const cgs: Array<{ id: number, firstName: string, lastName: string }> = response.caregivers
    const mappedCgs = cgs.map(c => {
      return {
        id: c.id,
        name: c.firstName + ' ' + c.lastName
      }
    })
    setCaregivers(mappedCgs)
    setVerificationInfo(response)
  }
  const loadDocs = async (): Promise<void> => {
    const { response } = await sendGet(`/ProgramVerification/Find?id=${id ?? ''}`, {})
    const loadedFiles: DocumentData[] = response.documents
    const files: DocumentFile[] = loadedFiles.map(d => {
      return { doc: d, file: undefined, editMode: false, documentTypeId: d.documentTypeID }
    })
    setFiles(files)
  }

  useEffect(() => {
    void loadData()
  }, [id, refreshTime])
  useEffect(() => {
    void loadDocs()
  }, [id])
  const disabled = verificationInfo.status === 'Verified' || readOnly

  const handleDocAttached = async (docTypeId: number, existingDocuments: DocumentData[], currentFiles: DocumentFile[]): Promise<void> => {
    const newAttachedFiles = existingDocuments.map(d => {
      const x: DocumentFile = { doc: d, file: undefined, editMode: false, documentTypeId: docTypeId }
      return x
    })
    const newList = [...currentFiles, ...newAttachedFiles]
    await handleFilesChanged(docTypeId, newList)
  }
  const handleDocUpload = async (docTypeId: number, file: File, currentFiles: DocumentFile[], target: DocumentTarget): Promise<void> => {
    if (file !== null) {
      const cgs: DocumentCaregiver[] = caregivers.map(c => {
        return {
          caregiverID: c.id,
          caregiverName: c.name,
          selected: false
        }
      })
      const newFile: DocumentData = {
        documentTarget: target,
        incomeRelated: false,
        childName: verificationInfo.childName,
        caregivers: cgs,
        fileName: file.name,
        documentID: index,
        verified: false
      } as DocumentData
      setIndex(index - 1)
      const newList = [...currentFiles, { doc: newFile, file, editMode: true, documentTypeId: docTypeId }]
      await handleFilesChanged(docTypeId, newList)
    }
  }
  const updateMembers = async (val: number): Promise<void> => {
    setVerificationInfo({ ...verificationInfo, memberCount: val })
  }
  return <>
    <Typography variant='h3' gutterBottom>{verificationInfo.programName} Verification Details - {verificationInfo.childName}</Typography>
    <Breadcrumbs>
      <Link onClick={goBack} sx={{ textTransform: 'none', color: 'inherit', textDecorationLine: 'none', cursor: 'pointer' }}>Return to {returnText}</Link>
    </Breadcrumbs>
    <Grid container spacing={2} >
      <Grid item xs={12} lg={6} sx={{ pt: 0, mt: '10px' }}>
        <Stack direction='column' spacing={2}>
          <ProgramVerification showHeader verificationId={ id ?? ''} redirectText='Income Approval Details' hideViewButton />
          <IncomeVerification data={verificationInfo} updateMembers={updateMembers}
            years={years} disabled={disabled}
            files={files.filter(f => f.documentTypeId === verificationInfo.requiredDocType)} caregivers={caregivers}
            setFiles={async (incomeFiles: DocumentFile[]) => {
              await handleFilesChanged(verificationInfo.requiredDocType ?? 0, incomeFiles)
            }}
            incomeDiscrepencyApprovalChanged={handleDiscrepencyApprovalChange}
          />
        </Stack>
      </Grid>
      <Grid item xs={12} lg={6}>
        {verificationInfo.optionalDocTypes?.length > 0 && (

          <LargeCard className='CardDisplay' sx={{ marginBottom: '20px' }} fullWidth content={
            <>
              <Typography variant='h6' sx={{ fontWeight: 'bold' }} gutterBottom>Additional Document Requirements</Typography>
              {verificationInfo.optionalDocTypes?.map(d => {
                const filteredFiles = files.filter(f => f.documentTypeId === d.documentTypeID)
                return <GeneralDocumentAccordion
                  childName={verificationInfo.childName}
                  disabled={disabled}
                  docTypeId={d.documentTypeID}
                  documentTarget={d.documentTarget ?? DocumentTarget.ADULT}
                  childId={verificationInfo.childID} id={verificationInfo.id}
                  years={years}
                  onDocumentUploaded={async (file: File) => {
                    await handleDocUpload(d.documentTypeID, file, filteredFiles, d.documentTarget ?? DocumentTarget.ADULT)
                  } }
                  onDocumentAttached={async (docs: DocumentData[]) => {
                    await handleDocAttached(d.documentTypeID, docs, filteredFiles)
                  } } title={d.name} summary={d.documentDescription ?? ''} tag={''}
                  files={filteredFiles}
                  setFiles={async (files: DocumentFile[]) => {
                    await handleFilesChanged(d.documentTypeID, files)
                  }}
                />
              })}
            </>}
          />
        )}
        <Box mb={'20px'}>
          <LargeCard className='CardDisplay' fullWidth
            content={
              <>
                <Typography variant='h6' sx={{ fontWeight: 'bold' }} gutterBottom>Notes</Typography>
                <TextAreaWithLabel label='' name='notes' onChange={(e) => {
                  setVerificationInfo({ ...verificationInfo, notes: e.target.value ?? '' })
                }} value={verificationInfo?.notes ?? ''} disabled={disabled}
                  required={false} showRequiredText={!disabled}
                />
              </>

      }
          />
        </Box>
      </Grid>
    </Grid>

    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mt: '10px' }}>
      <Button variant='outlined' sx={{ textTransform: 'none' }} onClick={goBack}>Return</Button>
      {!disabled && (
        <Button variant='contained' sx={{ textTransform: 'none' }} onClick={save}>Save Verification</Button>
      )}
    </Box>
  </>
}
